import 'react-day-picker/lib/style.css';

import { useState } from 'react';

import Card from '@/components/Card';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import DeleteConfirmModal from '@/modals/DeleteConfirmModal';
import calendarService from '@/services/calendar.service';
import moment from 'moment-timezone';

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 6; hour <= 23; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const mm = String(min).padStart(2, '0');
      const hh = String(hour).padStart(2, '0');
      options.push({
        label: `${displayHour}:${mm} ${ampm}`,
        value: `${hh}:${mm}`,
      });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const friendlyTimezone = (tz) => {
  if (!tz) return '';
  const abbr = moment.tz(tz).format('z');
  const city = tz.split('/').pop().replace(/_/g, ' ');
  return `${abbr} (${city})`;
};

const labelForTime = (value) => {
  const match = TIME_OPTIONS.find((t) => t.value === value);
  return match ? match.label : value;
};

const BlockOutTime = ({ providerId, platformId, timezone, onComplete }) => {
  const [date, setDate] = useState(undefined);
  const [startTime, setStartTime] = useState('12:00');
  const [endTime, setEndTime] = useState('13:00');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const timesValid = startTime < endTime;
  const isValid = !!date && timesValid;

  const dateStr = date ? moment(date).format('YYYY-MM-DD') : null;

  const handleBlockOut = async () => {
    setShowConfirm(false);
    setLoading(true);
    setMessage(null);

    try {
      const blockStart = moment
        .tz(`${dateStr} ${startTime}`, 'YYYY-MM-DD HH:mm', timezone)
        .toISOString();
      const blockEnd = moment
        .tz(`${dateStr} ${endTime}`, 'YYYY-MM-DD HH:mm', timezone)
        .toISOString();

      const { windowsAffected, appointmentConflicts } =
        await calendarService.blockOutTime(
          providerId,
          platformId,
          timezone,
          blockStart,
          blockEnd,
        );

      if (windowsAffected === 0) {
        setMessage({
          type: 'info',
          text: 'You had no bookable availability during that time, so nothing changed.',
        });
      } else {
        const conflictNote =
          appointmentConflicts > 0
            ? ` Heads up: you have ${appointmentConflicts} existing appointment${
                appointmentConflicts !== 1 ? 's' : ''
              } during this time — they were NOT cancelled. Contact those patients if needed.`
            : '';
        setMessage({
          type: appointmentConflicts > 0 ? 'warning' : 'success',
          text: `Blocked out ${labelForTime(startTime)}–${labelForTime(
            endTime,
          )} on ${moment(date).format(
            'ddd, MMM D',
          )}. Patients can no longer book this time.${conflictNote}`,
        });
      }

      onComplete();
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'An error occurred while blocking out this time. Your availability was not changed — please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const messageStyles = {
    success: 'bg-green-50 text-green-700',
    warning: 'bg-yellow-50 text-yellow-800',
    info: 'bg-blue-50 text-blue-700',
    error: 'bg-red-50 text-red-700',
  };

  return (
    <>
      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-bold text-lg">Block Out Time</div>
            {timezone && (
              <span className="text-sm text-gray-500">
                Times shown in {friendlyTimezone(timezone)}
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600">
            Remove a span from your bookable availability for a single day (for
            example, a lunch break or an appointment elsewhere). Patients will
            not be able to book the blocked time.
          </p>

          <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <DayPickerInput
                value={date}
                onDayChange={(day) => setDate(day)}
                placeholder="Select date"
                dayPickerProps={{ selectedDays: date }}
                inputProps={{
                  className:
                    'block w-full rounded-md border-gray-300 shadow-sm focus:border-mmj-blue focus:ring-mmj-blue sm:text-sm',
                  readOnly: true,
                }}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start
              </label>
              <select
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-mmj-blue focus:ring-mmj-blue sm:text-sm"
              >
                {TIME_OPTIONS.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End
              </label>
              <select
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-mmj-blue focus:ring-mmj-blue sm:text-sm"
              >
                {TIME_OPTIONS.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!timesValid && (
            <p className="text-sm text-red-500">
              The end time must be after the start time.
            </p>
          )}

          {message && (
            <div
              className={`text-sm p-3 rounded-lg ${
                messageStyles[message.type] || messageStyles.info
              }`}
            >
              {message.text}
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={!isValid || loading}
            className={`px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
              isValid && !loading
                ? 'bg-mmj-blue hover:opacity-90'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {loading ? 'Blocking...' : 'Block Out Time'}
          </button>
        </div>
      </Card>

      <DeleteConfirmModal
        show={showConfirm}
        title="Block out this time?"
        description={
          <div className="space-y-2">
            <p>
              This removes{' '}
              <strong>
                {labelForTime(startTime)}–{labelForTime(endTime)}
                {date ? ` on ${moment(date).format('dddd, MMMM D')}` : ''}
              </strong>{' '}
              from your bookable availability. Patients will no longer be able
              to book during this time.
            </p>
            <p>
              Any availability outside this span stays exactly as it is, and
              existing appointments are <strong>not</strong> cancelled.
            </p>
          </div>
        }
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleBlockOut}
      />
    </>
  );
};

export default BlockOutTime;
